import { Box, Button, Icon, IconType } from 'lucid-components-react';
import React, { ReactNode, useEffect, useState } from 'react';

import { LucidTheme } from 'types/types';

interface Props<T> {
  items: T[];
  pageSize: number;
  ellipsisDistance?: number;
  neighborDistance?: number;
  tailDistance?: number;
  alwaysRender?: boolean;
  render: (visibleItems: T[]) => ReactNode;
}

const PaginatorNew = <T extends object>({
  items,
  pageSize,
  ellipsisDistance = 5, // How many items away from the start/finish before showing ellipsis
  neighborDistance = 2, // How many items before and after the current one to always show
  tailDistance = 1, // How many items outside ellipses
  alwaysRender = false,
  render
}: Props<T>) => {
  const [activePage, setActivePage] = useState(1);
  const totalPageCount = Math.ceil(items.length / pageSize);
  const showPrev = activePage > 1;
  const showNext = activePage < totalPageCount;
  const visibleItemStart = (activePage - 1) * pageSize;
  const visibleItemEnd = activePage * pageSize;
  const visibleItems = items.slice(visibleItemStart, visibleItemEnd);
  const isSequential = (vals: number[]) => vals[1] === vals[0] + 1;
  const renderedChildren = render(visibleItems);
  let preChildren;
  let postChildren;

  if (alwaysRender) {
    const preInvisibleItems = items.slice(0, visibleItemStart);
    if (preInvisibleItems.length > 0) {
      preChildren = render(preInvisibleItems);
    }

    const postInvisibleItems = items.slice(visibleItemEnd);
    if (postInvisibleItems.length > 0) {
      postChildren = render(postInvisibleItems);
    }
  }

  const visiblePages = [...Array(totalPageCount).keys()]
    .map(i => i + 1)
    .filter(currentPage => {
      const isWithinStartTail = currentPage <= tailDistance;
      const isWithinEndTail = currentPage >= totalPageCount - tailDistance + 1;
      const isWithinNeighborDistance =
        Math.abs(currentPage - activePage) <= neighborDistance;
      const isWithinFirstEllipsisDistance =
        activePage <= ellipsisDistance &&
        currentPage <= ellipsisDistance + neighborDistance;
      const isWithinLastEllipsisDistance =
        activePage > totalPageCount - ellipsisDistance &&
        currentPage > totalPageCount - ellipsisDistance - neighborDistance;

      return (
        isWithinStartTail ||
        isWithinEndTail ||
        isWithinNeighborDistance ||
        isWithinFirstEllipsisDistance ||
        isWithinLastEllipsisDistance
      );
    });

  if (visiblePages.length > ellipsisDistance) {
    const firstTwoItems = visiblePages.slice(
      tailDistance - 1,
      tailDistance + 1
    );
    const lastTwoItems = visiblePages.slice(
      -(tailDistance + 1),
      visiblePages.length - tailDistance + 1
    );

    if (!isSequential(firstTwoItems)) {
      const startEllipsisId = -1;
      visiblePages.splice(tailDistance, 0, startEllipsisId);
    }

    if (!isSequential(lastTwoItems)) {
      const endEllipsisId = -2;
      visiblePages.splice(visiblePages.length - tailDistance, 0, endEllipsisId);
    }
  }

  useEffect(() => {
    setActivePage(1);
  }, [items]);

  return (
    <Box>
      <Box css={{ display: 'none' }}>{preChildren}</Box>
      {renderedChildren}
      <Box css={{ display: 'none' }}>{postChildren}</Box>
      {totalPageCount > 1 && (
        <Box style={{ textAlign: 'right' }}>
          {showPrev && (
            <Button
              asLink
              css={(theme: LucidTheme) => ({
                color: theme.colors.coolGrey[50],
                verticalAlign: 'middle',
                outline: 'none',
                '&:hover, &:focus': {
                  color: theme.colors.indigo[60]
                }
              })}
              onClick={() => setActivePage(activePage - 1)}
            >
              <Icon
                css={{
                  fontSize: '1rem',
                  transform: 'rotate(180deg)',
                  marginRight: '1rem'
                }}
                type={IconType.CarotRight}
              />
            </Button>
          )}
          {visiblePages.map(currentPage =>
            currentPage > 0 ? (
              <Button
                key={currentPage}
                active={activePage === currentPage}
                css={(theme: LucidTheme) => [
                  {
                    display: 'inline-block',
                    padding: 0,
                    width: '1.75rem',
                    height: '1.75rem',
                    fontSize: '1rem',
                    color: theme.colors.coolGrey[40],
                    borderRadius: '2px',
                    border: `1px solid ${theme.colors.coolGrey[20]}`,
                    boxShadow: 'none',
                    margin: '0 .5rem',
                    outline: 'none',
                    '&:hover, &:focus': {
                      color: theme.colors.indigo[60],
                      backgroundColor: theme.buttons.secondary.backgroundColor,
                      border: `1px solid ${theme.colors.indigo[60]}`
                    }
                  },
                  activePage === currentPage && {
                    '&, &:hover, &:focus': {
                      backgroundColor: theme.colors.indigo[60],
                      color: theme.buttons.primary.color,
                      border: `1px solid ${theme.colors.indigo[60]}`
                    }
                  }]}
                onClick={() => setActivePage(currentPage)}
                primary={activePage === currentPage}
              >
                {currentPage}
              </Button>
            ) : (
                /* eslint-disable-next-line react/jsx-indent */
                <span
                  key={currentPage}
                  css={{
                    display: 'inline-block',
                    textAlign: 'center',
                    margin: '0 .5rem',
                    width: '1.75rem'
                  }}
                >
                  ...
                </span>
              )
          )}
          {showNext ? (
            <Button
              asLink
              css={(theme: LucidTheme) => ({
                color: theme.colors.coolGrey[50],
                verticalAlign: 'middle',
                outline: 'none',
                '&:hover, &:focus': {
                  color: theme.colors.indigo[60]
                }
              })}
              onClick={() => setActivePage(activePage + 1)}
            >
              <Icon
                css={{
                  fontSize: '1rem',
                  marginLeft: '1rem'
                }}
                type={IconType.CarotRight}
              />
            </Button>
          ) : (
              /* eslint-disable-next-line react/jsx-indent */
              <span css={{ marginLeft: '2rem' }}></span>
            )}
        </Box>
      )}
    </Box>
  );
};

export default PaginatorNew;
